import {useMemo} from 'react'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {useQuery} from '@tanstack/react-query'
import {Trans} from '@lingui/react/macro'
import {useEventCallback} from 'usehooks-ts'

import {ONE_MINUTE} from '#/lib/constants.ts'
import {useAgent, useSession, useWsocialVerified} from '#/state/session'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {
  useWIDLockByAgeUpgradeControl,
  useWIDUpgradeControl,
} from '#/components/dialogs/Context.tsx'
import * as Toast from '#/components/Toast'

// Identifies a feature that may require W Identity verification.
// When the server endpoint is available, BLOCKED_FOR_UNVERIFIED will be
// replaced with the API response. Until then, all permissions are blocked.
export type VerificationPermission =
  | 'post' // composing new posts
  | 'like' // liking posts, feeds, labelers
  | 'repost' // reposting content
  | 'follow' // following or unfollowing users
  | 'message' // direct messages and DM settings
  | 'share' // sharing content externally
  | 'pin_feed' // pinning or unpinning feeds and lists
  | 'edit_list' // creating or editing curate/mod lists
  | 'starter_pack' // starter pack actions (join, follow-all)
  | 'edit_profile' // saving changes to own profile
  | 'block' // blocking or unblocking users
  | 'report' // reporting posts, users, starter packs, or lists
  | 'post_menu' // opening the post context menu
  | 'profile_menu' // opening the profile context menu
  | 'subscribe_notifications' // subscribing to activity notifications
  | 'profile_tab' // switching tabs on a profile page

// Full list of permissions blocked for unverified users.
// Replace with the server response once the endpoint is available.
const BLOCKED_FOR_UNVERIFIED: VerificationPermission[] = [
  'post',
  'like',
  'repost',
  'follow',
  'message',
  'share',
  'pin_feed',
  'edit_list',
  'starter_pack',
  'edit_profile',
  'block',
  'report',
  'post_menu',
  'profile_menu',
  'subscribe_notifications',
  'profile_tab',
]

export const useVerificationToast = () => {
  const agent = useAgent()
  const {_} = useLingui()
  const wsocialVerified = useWsocialVerified()
  const session = useSession()
  const widUpgradeControl = useWIDUpgradeControl()
  const widLockByAgeControl = useWIDLockByAgeUpgradeControl()
  const {requestSwitchToAccount} = useLoggedOutViewControls()
  const {data, isLoading} = useQuery({
    queryKey: ['account-get-capabilities', session.currentAccount?.did],
    queryFn: ({signal}) => agent.account.getCapabilities({signal}),
    enabled: !!agent && !!session.currentAccount && !wsocialVerified,
    staleTime: ONE_MINUTE * 5,
  })

  const blockedFeatures = useMemo(() => {
    if (wsocialVerified) return []
    if (!data || isLoading) return [...BLOCKED_FOR_UNVERIFIED]

    return [...BLOCKED_FOR_UNVERIFIED].filter(f => {
      switch (f) {
        case 'like':
          return !data.unverifiedLikesAllowed
        case 'follow':
          return !data.unverifiedFollowsAllowed
        case 'pin_feed': {
          return false
        }
        case 'profile_tab':
        case 'subscribe_notifications':
          return true
        default:
          return true
      }
    })
  }, [data, isLoading, wsocialVerified])

  const gatedOpenComposer = useEventCallback(
    (permission: VerificationPermission): boolean => {
      if (!session.hasSession) {
        //requestSwitchToAccount({requestedAccount: 'none'})
        return true
      }
      if (wsocialVerified) return true
      if (!wsocialVerified && blockedFeatures.includes(permission)) {
        // Don't open widUpgrade if age-lock overlay is already showing —
        // widLockByAgeControl already handles the verification flow.
        if (!widLockByAgeControl.isOpen) {
          widUpgradeControl.open()
        }
        return false
      }
      return true
    },
  )

  return gatedOpenComposer
}
